import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import {
  Carrier,
  CarrierModelKeys,
  ProductionReconciliationCarrier,
  ProductionReconciliationCarrierMetrics,
  ProductionReconciliationMetricKey,
  ProductionReconciliationRecord,
  ProductionReconciliationRecordKeys,
} from '@ag-common-lib/public-api';
import {
  formatFullDateRange,
  getReportingPeriodIndicesThroughDate,
  getReportingPeriodRange,
} from './reporting-week.utils';

export const RECONCILIATION_COLLECTION = 'reconciliation';
export const CARRIERS_COLLECTION = 'carriers';

export type ProductionReconciliationPresetMode = 'ytd' | 'range';

export type ProductionReconciliationSelectOption<T extends string | number> = {
  id: T;
  text: string;
};

export const RECONCILIATION_MODE_OPTIONS: ProductionReconciliationSelectOption<ProductionReconciliationPresetMode>[] = [
  { id: 'ytd', text: 'YTD' },
  { id: 'range', text: 'Range' },
];

export enum ProductionReconciliationDashboardColumnKey {
  carrierName = 'carrierName',
}

export interface ProductionReconciliationDualValue {
  current: number | null;
  compare: number | null;
}

export type ProductionReconciliationDashboardRow = {
  id: string;
  [ProductionReconciliationDashboardColumnKey.carrierName]: string;
} & Record<string, ProductionReconciliationDualValue | string>;

export interface ProductionReconciliationPeriodColumn {
  periodIndex: number;
  caption: string;
  metrics: Array<{
    key: ProductionReconciliationMetricKey;
    label: string;
    dataField: string;
    format: 'currency' | 'number';
  }>;
}

export interface ProductionReconciliationDashboardSetup {
  mode: ProductionReconciliationPresetMode;
  currentYear: number;
  compareYear: number;
  stopDate: string;
  currentYearPeriods: number[];
}

export interface ProductionReconciliationDashboardData {
  periods: ProductionReconciliationPeriodColumn[];
  rows: ProductionReconciliationDashboardRow[];
}

const RECONCILIATION_RECORD_METADATA_KEYS = new Set<string>([
  ProductionReconciliationRecordKeys.year,
  ProductionReconciliationRecordKeys.week,
  ProductionReconciliationRecordKeys.range,
  ...Object.values(BaseModelKeys),
]);

export function buildProductionReconciliationMetricFieldName(
  periodIndex: number,
  metricKey: ProductionReconciliationMetricKey,
): string {
  return `period${periodIndex}_${metricKey}`;
}

export function resolveReportingPeriodIndices(setup: ProductionReconciliationDashboardSetup): number[] {
  if (setup.mode === 'range') {
    return [...new Set(setup.currentYearPeriods)].sort((left, right) => left - right);
  }

  return getReportingPeriodIndicesThroughDate(setup.currentYear, setup.stopDate);
}

export function buildReportingPeriodOptionLabel(year: number, periodIndex: number): string {
  const range = getReportingPeriodRange(year, periodIndex);
  console.log('buildReportingPeriodOptionLabel range');
  return `Week ${periodIndex} (${formatFullDateRange(range.start, range.end)})`;
}

export function getCarrierMetricsFromRecord(
  record: ProductionReconciliationRecord | undefined,
  carrierId: string,
): ProductionReconciliationCarrierMetrics | null {
  if (!record) {
    return null;
  }

  const value = record[carrierId];

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const metrics = value as Partial<ProductionReconciliationCarrierMetrics>;

  if (metrics.submitted == null && metrics.paid == null) {
    return null;
  }

  return {
    submitted: metrics.submitted ?? 0,
    paid: metrics.paid ?? 0,
  };
}

export function isReconciliationRecordMetadataKey(key: string): boolean {
  return RECONCILIATION_RECORD_METADATA_KEYS.has(key);
}

export function mapCarriersForReconciliationDashboard(carriers: Carrier[]): ProductionReconciliationCarrier[] {
  return carriers
    .filter(carrier => Boolean(carrier?.dbId && carrier?.[CarrierModelKeys.carrierName] && !carrier?.isDeleted))
    .map(carrier => ({
      id: carrier.dbId!,
      name: carrier[CarrierModelKeys.carrierName]!,
    }))
    .sort((left, right) => left.name.localeCompare(right.name));
}
